import React, { FC, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import {
  colors,
  MOBILE_BREAKPOINT,
  PARTNER_CAMPAIGN_CODES,
  TABLET_BREAKPOINT,
} from '../../constants';
import { parseQueryString } from '../../utilities/queryServices';
import { PrimaryButton } from '../Button';
import { OfferGlance } from '../OfferGlance';

import { ActionPill, ActionPillContainer, ActionPillProps } from './ActionPill';
import { NavLogos } from './NavLogos';
import { NavProgressBar } from './NavProgressBar';
import { LogoContainer, MainNavContent, NavContainer } from './NavStyles';

// Nav

// Displays ProsperLogo, ProgressBar, and OfferGlance at the top of a page after Apply
// Render as the top-level first child of Page

// Interface --------------------------------------------------------------------------------------
interface NavProps {
  loading?: boolean;
  showSkipLink?: boolean;
}

// Styles -----------------------------------------------------------------------------------------
// Centers the ProgressBar
const ProgressContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  pointer-events: none;
`;
ProgressContainer.displayName = 'ProgressContainer';

const ProgressCenter = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  z-index: 2;
  height: 100%;
  margin: 0;
  display: flex;
  pointer-events: none;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    top: 3.9375rem;
    height: 2.25rem;
    width: 100%;
  }
`;
ProgressCenter.displayName = 'ProgressCenter';

const SkipLink = styled(PrimaryButton)`
  z-index: 999;
  position: absolute;
  top: 0;
  left: -62.4735rem;
  overflow: hidden;
  margin: 1rem 0 0 1.5rem;
  border: none;
  padding: 1rem 2rem;
  transition: none;
  &:focus,
  &:focus-visible {
    left: 0;
    border: 0.125rem solid ${colors.BUTTON_PRIMARY};
    box-shadow: 0 0 0 0.0625rem #fff inset;
    background: ${colors.UI_PRIMARY};
  }
  @media (max-width: ${TABLET_BREAKPOINT}) {
    margin: 0.625rem 0 0 1rem;
    padding: 0.5rem 1.5rem;
    height: 3rem;
    font-size: 1rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0.5rem 0 0 0.5rem;
    padding: 0 1rem;
    font-size: 0.875rem;
  }
`;
SkipLink.displayName = 'SkipLink';

// Component --------------------------------------------------------------------------------------
export const Nav: FC<NavProps & ActionPillProps> = ({
  loading,
  showSkipLink,
  onSignOutClick,
}) => {
  const [progressState, setProgressState] = useState('');
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [offerGlanceDrawerActive, setOfferGlanceDrawerActive] = useState(false);
  const [partnerCampaign, setPartnerCampaign] = useState('');

  const { creditCardApplication } = useContext(CreditCardApplicationContext);
  const { applicationId, offerCheck } = creditCardApplication || {};
  const { offer } = offerCheck || {};
  const { creditLimit, conditionalOnCashflowVerification } = offer || {};

  // Tells ProgressBar which breadcrumb should be active
  const getProgressState = () => {
    const { pathname } = window.location;
    if (pathname.includes('offer') && !pathname.includes('waiting-room')) {
      setProgressState('REVIEW OFFER');
      setShowProgressBar(true);
      return;
    }
    if (
      pathname.includes('connect-to-bank') ||
      pathname.includes('verification') ||
      (pathname.includes('auto-pay') && conditionalOnCashflowVerification)
    ) {
      setProgressState('CONNECT BANK');
      setShowProgressBar(true);
      return;
    }
    if (
      pathname.includes('payment-date') ||
      pathname.includes('export') ||
      pathname.includes('finish-up') ||
      (pathname.includes('auto-pay') && !conditionalOnCashflowVerification)
    ) {
      setProgressState('FINISH UP');
      setShowProgressBar(true);
      return;
    }
    setShowProgressBar(false);
  };

  // Returns partner bank ref code from query parameter to show partner bank logo on navbar
  const getPartnerCampaign = () => {
    const queryMap = parseQueryString(
      decodeURIComponent(window.location.search)
    );
    const crcValue = queryMap.get('crc')?.trim();
    if (crcValue && crcValue !== '') {
      const crcUppercased = crcValue.toUpperCase();
      return PARTNER_CAMPAIGN_CODES.find((code) =>
        crcUppercased.startsWith(`${code}-`)
      );
    }
    return '';
  };

  const isPostOfferFunnelPage = () =>
    window.location.pathname.includes('application') &&
    !window.location.pathname.includes('offer-waiting-room') &&
    !window.location.pathname.includes('agreements') &&
    !window.location.pathname.includes('decline');

  // Used to determine two-step action pill behavior
  // ie. we don't show the offer glance until bank connect (and show the sign out button instead)
  const showTwoStepBehavior = () =>
    conditionalOnCashflowVerification &&
    isPostOfferFunnelPage() &&
    window.location.pathname.includes('offer');

  // Show OfferGlance component for funnel pages after offer-waiting-room
  const showOfferGlance = () =>
    !loading &&
    isPostOfferFunnelPage() &&
    !showTwoStepBehavior() &&
    typeof applicationId === 'string' &&
    applicationId.length > 0 &&
    creditLimit;

  // Show ActionPill component on these pages: Apply, Decline, and Agreements
  const showActionPill = () =>
    !loading &&
    !window.location.pathname.includes('offer-waiting-room') &&
    !window.location.pathname.includes('annual-income') &&
    (!isPostOfferFunnelPage() || showTwoStepBehavior());

  useEffect(() => {
    setPartnerCampaign(getPartnerCampaign());
  }, []);

  useEffect(() => {
    getProgressState();
  }, [creditCardApplication, loading]);

  // Functions ------------------------------------------------------------------------------------
  const handleSkip = () => {
    if (window && window.location) {
      window.location.assign('#main');
    }
  };

  // Render ---------------------------------------------------------------------------------------
  return (
    <NavContainer offerGlanceOpen={offerGlanceDrawerActive}>
      {showSkipLink && (
        <SkipLink
          id="skip-link"
          role="link"
          type="button"
          onClick={() => handleSkip()}
        >
          Skip to main content
        </SkipLink>
      )}
      <MainNavContent
        showProgressBar={showProgressBar && applicationId.length > 0}
      >
        <LogoContainer showProgressBar={showProgressBar}>
          <NavLogos partnerCampaign={partnerCampaign} />
        </LogoContainer>
        <ProgressContainer>
          {showProgressBar && applicationId.length > 0 && (
            <ProgressCenter>
              <NavProgressBar progressState={progressState} />
            </ProgressCenter>
          )}
        </ProgressContainer>
        {showOfferGlance() && (
          <OfferGlance
            conditionalOnCashflowVerification={
              conditionalOnCashflowVerification
            }
            active={offerGlanceDrawerActive}
            setActive={setOfferGlanceDrawerActive}
          />
        )}
        {showActionPill() && (
          <ActionPillContainer showProgressBar={showProgressBar}>
            <ActionPill onSignOutClick={onSignOutClick} />
          </ActionPillContainer>
        )}
      </MainNavContent>
    </NavContainer>
  );
};

Nav.defaultProps = {
  loading: false,
  showSkipLink: false,
};
